/**
 * ч.1373 — «Рахунок на оплату» за зразком 1С (рішення власника: «нашу друковану форму рахунку
 * і її ж в Excel — по зразку 1С»).
 *
 * Одна модель для двох виходів: сторінка `CustomerInvoicePrintPage` малює з неї бланк, а
 * «зберегти в Excel» шле її ж на `POST /print/xlsx/invoice` — сервер складає книгу на сітці
 * 1С (`services/print_invoice_xlsx.py`). Так файл і екран не розходяться.
 */
import { amountToWordsUAFull } from '@/utils/numberToWordsUA';
import { extractMfoFromIban, findBankByIban, type BankRow } from '@/shared/ukr_classifiers';
import { partyCodeParts, orgOwnCode, type PartyLike } from '@/shared/utils/partyCodes';
import { printLineAmount, vatMode, type VatMode } from '@/shared/utils/vatFlags';

/** Текст попередження 1С над рахунком (лише коли в рахунку є товари). */
export const INVOICE_NOTICE =
  'Увага! Оплата цього рахунку означає погодження з умовами поставки товарів. Повідомлення '
  + "про оплату є обов'язковим, в іншому випадку не гарантується наявність товарів на складі. "
  + 'Товар відпускається за фактом надходження коштів на р/р Постачальника, самовивозом, за '
  + 'наявності довіреності та паспорта.';

const MONTHS_GEN = ['січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня'];

const PT_LABELS: Record<string, string> = {
  PREPAY: 'повна передплата',
  PARTIAL: 'часткова передплата',
  POSTPAY: 'постоплата',
  ON_DELIVERY: 'оплата при отриманні',
};

export const fmtMoney = (n: number | string) =>
  Number(n).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtQty = (n: number) => n.toLocaleString('uk-UA', { maximumFractionDigits: 3 });

const fmtDate = (s: string) => new Date(s).toLocaleDateString('uk-UA');

/** «26 жовтня 2026 р.» */
export function dateLongUA(s: string): string {
  const d = new Date(s);
  return `${d.getDate()} ${MONTHS_GEN[d.getMonth()]} ${d.getFullYear()} р.`;
}

export interface InvoiceParty extends PartyLike {
  name: string;
  full_name?: string | null;
  legal_address?: string | null;
  phone?: string | null;
}

export interface InvoiceDocLine {
  nomenclature_id: string | null;
  quantity: string;
  price: string;
  discount_percent?: string;
  amount_without_vat: string;
  vat_amount: string;
  service_description?: string | null;
}

export interface InvoiceDoc {
  document_number: string;
  document_date: string;
  items: InvoiceDocLine[];
  /** ч.1374: ознаки ПДВ документа (як у 1С); без них — ціни без ПДВ. */
  prices_include_vat?: boolean;
  vat_considered?: boolean;
  payment_terms_type?: string | null;
  prepayment_percent?: string | null;
  prepayment_due_date?: string | null;
  final_payment_days?: number | null;
  payment_due_date?: string | null;
  delivery_date?: string | null;
  delivery_days?: number | null;
  delivery_date_fixed?: boolean;
  incoterms?: string | null;
  delivery_address?: string | null;
  consignee_name?: string | null;
}

/**
 * Реквізити рахунку/замовлення для друку — лише заповнені (рішення власника 2026-07-26 і
 * ч.1374: усе, що заповнено в документі, має бути на друку). Спільне для рахунку покупцю,
 * замовлення покупця і замовлення/рахунку постачальника.
 */
export function orderRequisites(doc: InvoiceDoc, total: number,
  baseOrder?: { document_number: string; document_date: string } | null): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  if (doc.payment_due_date) out.push(['Сплатити до', fmtDate(doc.payment_due_date)]);
  if (doc.payment_terms_type) {
    let t = PT_LABELS[doc.payment_terms_type] ?? doc.payment_terms_type;
    if (doc.payment_terms_type === 'PARTIAL' && doc.prepayment_percent) {
      const pct = Number(doc.prepayment_percent);
      t += ` ${pct}% (${fmtMoney(total * pct / 100)} грн)`;
      if (doc.final_payment_days != null) t += `, решта — протягом ${doc.final_payment_days} днів після поставки`;
    } else if (doc.payment_terms_type === 'POSTPAY' && doc.final_payment_days != null) {
      t += ` протягом ${doc.final_payment_days} днів після поставки`;
    }
    out.push(['Умови оплати', t]);
  }
  if (doc.payment_terms_type === 'PARTIAL' && doc.prepayment_due_date) {
    out.push(['Передоплата до', fmtDate(doc.prepayment_due_date)]);
  }
  // 0381: «N днів з моменту оплати» — до фіксації текст умови, після — дата.
  if (doc.delivery_days != null && !doc.delivery_date_fixed) {
    out.push(['Термін поставки', `${doc.delivery_days} календарних днів з моменту `
      + (doc.payment_terms_type === 'PARTIAL' || doc.payment_terms_type === 'PREPAY'
        ? 'передоплати' : 'оплати')]);
  } else if (doc.delivery_date) {
    out.push(['Термін поставки', fmtDate(doc.delivery_date)]);
  }
  if (doc.incoterms) {
    out.push(['Умови постачання', doc.delivery_address && doc.incoterms !== 'EXW'
      ? `${doc.incoterms}, ${doc.delivery_address}` : doc.incoterms]);
  } else if (doc.delivery_address) {
    out.push(['Пункт постачання', doc.delivery_address]);
  }
  if (doc.consignee_name) out.push(['Вантажоодержувач', doc.consignee_name]);
  if (baseOrder) {
    out.push(['Підстава', `замовлення № ${baseOrder.document_number} від ${fmtDate(baseOrder.document_date)}`]);
  }
  return out;
}

export interface InvoiceInput {
  doc: InvoiceDoc;
  org: InvoiceParty & { is_vat_payer: boolean; director_name?: string | null };
  contractor?: InvoiceParty | null;
  /** Рядок договору (уже з урахуванням «друкувати / назва для друку» картки договору). */
  contractLine?: string | null;
  orgIban?: string | null;
  contractorIban?: string | null;
  banks?: BankRow[];
  nomenclature: Map<string, { name: string; is_service?: boolean; base_uom_id?: string | null }>;
  uoms: Map<string, string>;
  baseOrder?: { document_number: string; document_date: string } | null;
}

export interface InvoiceLine {
  name: string;
  qty: number;
  uom: string;
  price: number;
  discount: number | null;
  amount: number;
}

export interface InvoiceModel {
  notice: string;
  recipient: { name: string; code: string; iban: string; bank: string; mfo: string };
  title: string;
  supplierName: string;
  supplierDetails: string[];
  buyerName: string;
  buyerDetails: string[];
  contract: string;
  requisites: Array<[string, string]>;
  /** без ПДВ у документі / ціни без ПДВ / ціни з ПДВ. */
  mode: VatMode;
  hasDiscount: boolean;
  items: InvoiceLine[];
  totals: Array<[string, number]>;
  countLine: string;
  words: string[];
  signerName: string;
}

/** Коди сторони так, як друкує 1С: «код за ЄДРПОУ …, ІПН …». */
function codesLine(p: InvoiceParty, vatPayer: boolean | null | undefined): string {
  const parts = partyCodeParts(p).map((x) => (x.startsWith('ЄДРПОУ ') ? `код за ${x}` : x));
  if (vatPayer === false) parts.push('неплатник ПДВ');
  return parts.join(', ');
}

/** Рядки реквізитів сторони: рахунок, адреса з телефоном, коди. */
function partyDetails(p: InvoiceParty, iban: string | null | undefined,
  banks: BankRow[], vatPayer: boolean | null | undefined): string[] {
  const lines: string[] = [];
  if (iban) {
    const bank = findBankByIban(iban, banks);
    const mfo = extractMfoFromIban(iban);
    lines.push([`Р/р ${iban}`, bank ? `Банк ${bank}` : '', mfo ? `МФО ${mfo}` : '']
      .filter(Boolean).join(', '));
  }
  const addr = [p.legal_address?.trim(), p.phone?.trim() ? `тел.: ${p.phone.trim()}` : '']
    .filter(Boolean).join(', ');
  if (addr) lines.push(addr);
  const codes = codesLine(p, vatPayer);
  if (codes) lines.push(codes);
  return lines;
}

export function buildInvoiceModel(x: InvoiceInput): InvoiceModel {
  const { doc, org } = x;
  const banks = x.banks ?? [];
  const mode = vatMode(doc, org.is_vat_payer);
  const vatPayer = mode !== 'none';

  const items: InvoiceLine[] = doc.items.map((it) => {
    const nom = x.nomenclature.get(it.nomenclature_id ?? '');
    const svc = it.service_description?.trim();
    const disc = Number(it.discount_percent ?? 0);
    return {
      name: `${nom?.name ?? '—'}${svc ? ` — ${svc}` : ''}`,
      qty: Number(it.quantity),
      uom: nom?.base_uom_id ? (x.uoms.get(nom.base_uom_id) ?? 'шт') : 'шт',
      price: Number(it.price),
      discount: disc > 0 ? disc : null,
      amount: printLineAmount(it, mode),
    };
  });
  const totalNoVat = doc.items.reduce((s, it) => s + Number(it.amount_without_vat), 0);
  const totalVat = doc.items.reduce((s, it) => s + Number(it.vat_amount), 0);
  const total = totalNoVat + totalVat;
  // Попередження 1С — про відпуск товару; у рахунку лише на послуги воно ні до чого.
  const hasGoods = doc.items.some((it) => !x.nomenclature.get(it.nomenclature_id ?? '')?.is_service);

  const requisites = orderRequisites(doc, total, x.baseOrder);

  // Підсумки — за режимом ПДВ (зразок 1С: «Разом / Сума ПДВ / Усього з ПДВ»; з цінами з ПДВ —
  // «Усього / У тому числі ПДВ»; без ПДВ у документі — лише «Усього»).
  const totals: Array<[string, number]> = mode === 'none'
    ? [['Усього:', totalNoVat]]
    : mode === 'included'
      ? [['Усього:', total], ['У тому числі ПДВ:', totalVat]]
      : [['Разом:', totalNoVat], ['Сума ПДВ:', totalVat], ['Усього з ПДВ:', total]];
  const printedTotal = mode === 'none' ? totalNoVat : total;
  const words = [amountToWordsUAFull(printedTotal),
    vatPayer ? `У т.ч. ПДВ: ${amountToWordsUAFull(totalVat)}` : 'Без ПДВ'];

  const supplierName = org.full_name || org.name;
  return {
    notice: hasGoods ? INVOICE_NOTICE : '',
    recipient: {
      name: supplierName,
      code: orgOwnCode(org) ?? '',
      iban: x.orgIban ?? '',
      bank: (x.orgIban && findBankByIban(x.orgIban, banks)) || '',
      mfo: extractMfoFromIban(x.orgIban) ?? '',
    },
    title: `Рахунок на оплату № ${doc.document_number} від ${dateLongUA(doc.document_date)}`,
    supplierName,
    supplierDetails: partyDetails(org, x.orgIban, banks, org.is_vat_payer),
    buyerName: x.contractor ? (x.contractor.full_name || x.contractor.name) : '—',
    buyerDetails: x.contractor
      ? partyDetails(x.contractor, x.contractorIban, banks, x.contractor.is_vat_payer) : [],
    contract: x.contractLine ?? '',
    requisites,
    mode,
    hasDiscount: items.some((it) => it.discount != null),
    items,
    totals,
    countLine: `Всього найменувань ${items.length}, на суму ${fmtMoney(printedTotal)} грн`,
    words,
    signerName: org.director_name ?? '',
  };
}

/** Опис книги для `POST /print/xlsx/invoice` (поля `InvoiceXlsxRequest`). */
export function invoiceXlsxPayload(m: InvoiceModel, filename: string) {
  return {
    filename,
    notice: m.notice,
    recipient_name: m.recipient.name,
    recipient_code: m.recipient.code,
    recipient_iban: m.recipient.iban,
    recipient_bank: m.recipient.bank,
    recipient_mfo: m.recipient.mfo,
    title: m.title,
    supplier_name: m.supplierName,
    supplier_details: m.supplierDetails.join('\n'),
    buyer_name: m.buyerName,
    buyer_details: m.buyerDetails.join('\n'),
    contract: m.contract,
    requisites: m.requisites,
    vat_mode: m.mode,
    items: m.items.map((it) => ({
      name: it.name, qty: String(it.qty), uom: it.uom, price: it.price.toFixed(2),
      discount: it.discount != null ? String(it.discount) : null, amount: it.amount.toFixed(2),
    })),
    totals: m.totals.map(([label, v]) => [label, v.toFixed(2)] as [string, string]),
    count_line: m.countLine,
    words: m.words.join('\n'),
    signer_name: m.signerName,
  };
}

export type InvoiceXlsxPayload = ReturnType<typeof invoiceXlsxPayload>;
